import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe } from './icons';
import BaziAnalyzer from './BaziAnalyzer';
import CalendarTab from './CalendarTab';
import ErrorBoundary from './ErrorBoundary';
import Footer from './Footer';
import useHolidays from '../hooks/useHolidays';
import useLunarMonthData, { getTimezoneOffsetHours } from '../hooks/useLunarMonthData';
import useSelectedDateDetails from '../hooks/useSelectedDateDetails';
import { TEXTS } from '../utils/lunarConstants';
import { formatDateKey } from '../utils/lunarUtils';

const FAVORITES_STORAGE_KEY = 'lunar-favorites';
const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const loadFavorites = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('[favorites] Failed to load favorites', error);
    return [];
  }
};

const persistFavorites = (items) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.warn('[favorites] Failed to save favorites', error);
  }
};

export default function LunarCalendarApp() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [language, setLanguage] = useState('vi');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [favoriteDates, setFavoriteDates] = useState(loadFavorites);
  const [mainTab, setMainTab] = useState('calendar');
  const [timezoneOffsetHours, setTimezoneOffsetHours] = useState(() => getTimezoneOffsetHours(new Date()));
  const { holidays, holidayLookup, isLoading: holidaysLoading, error: holidaysError } = useHolidays();
  const { monthData, monthDataMap, monthlyHolidays } = useLunarMonthData({
    currentDate,
    holidays,
    holidayLookup,
    timezoneOffsetHours
  });

  useEffect(() => {
    persistFavorites(favoriteDates);
  }, [favoriteDates]);

  useEffect(() => {
    const updateTimezone = () => {
      setTimezoneOffsetHours(getTimezoneOffsetHours(new Date()));
    };

    document.addEventListener('visibilitychange', updateTimezone);
    return () => document.removeEventListener('visibilitychange', updateTimezone);
  }, []);

  const zodiacInfoMap = useMemo(() => ({
    'Tý': { element: { vi: 'Nước', en: 'Water' }, personality: { vi: 'Thông minh, táo bạo', en: 'Smart, bold' } },
    'Sửu': { element: { vi: 'Đất', en: 'Earth' }, personality: { vi: 'Kiên nhẫn, chăm chỉ', en: 'Patient, diligent' } },
    'Dần': { element: { vi: 'Gỗ', en: 'Wood' }, personality: { vi: 'Dũng cảm, lãnh đạo', en: 'Brave, leader-like' } },
    'Mão': { element: { vi: 'Gỗ', en: 'Wood' }, personality: { vi: 'Hiền lành, thích hòa hợp', en: 'Gentle, harmonious' } },
    'Thìn': { element: { vi: 'Đất', en: 'Earth' }, personality: { vi: 'Quyết đoán, tự tin', en: 'Decisive, confident' } },
    'Tỵ': { element: { vi: 'Lửa', en: 'Fire' }, personality: { vi: 'Khôn ngoan, bình tĩnh', en: 'Wise, calm' } },
    'Ngọ': { element: { vi: 'Lửa', en: 'Fire' }, personality: { vi: 'Năng động, vui vẻ', en: 'Energetic, cheerful' } },
    'Mùi': { element: { vi: 'Đất', en: 'Earth' }, personality: { vi: 'Tốt bụng, cẩn thận', en: 'Kind, careful' } },
    'Thân': { element: { vi: 'Kim', en: 'Metal' }, personality: { vi: 'Thông minh, hài hước', en: 'Clever, humorous' } },
    'Dậu': { element: { vi: 'Kim', en: 'Metal' }, personality: { vi: 'Trung thực, siêng năng', en: 'Honest, hardworking' } },
    'Tuất': { element: { vi: 'Đất', en: 'Earth' }, personality: { vi: 'Trung thành, tin cậy', en: 'Loyal, reliable' } },
    'Hợi': { element: { vi: 'Nước', en: 'Water' }, personality: { vi: 'Hòa nhân, tốt bụng', en: 'Affable, kind' } }
  }), []);

  const t = TEXTS[language] || TEXTS.vi;
  const favoriteSet = useMemo(() => new Set(favoriteDates), [favoriteDates]);
  const selectedDateKey = selectedDate ? formatDateKey(selectedDate) : null;
  const isSelectedFavorite = selectedDateKey ? favoriteSet.has(selectedDateKey) : false;

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const handleToggleFavorite = useCallback((date) => {
    if (!isValidDate(date)) {
      // eslint-disable-next-line no-console
      console.warn('[favorites] Invalid date provided', date);
      return;
    }
    const key = formatDateKey(date);
    setFavoriteDates((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ));
  }, []);

  const {
    selectedLunar,
    selectedZodiacAnimal,
    dayCanChi,
    auspiciousHours,
    selectedZodiacSign,
    selectedZodiacInfo,
    localizedDayName,
    localizedCanChiYear,
    zodiacDisplay,
    holidaysForSelectedDate,
    zodiacEmoji,
    westernZodiacSymbol,
    weekdayLabel,
    lunarStemBranchLines,
    selectedNotes,
    formatHourLabel
  } = useSelectedDateDetails({
    selectedDate,
    language,
    monthDataMap,
    timezoneOffsetHours,
    zodiacInfoMap,
    t
  });

  const getHolidayName = useCallback((holiday) => {
    if (!holiday) return '';
    if (language === 'vi') {
      return holiday.name_vi || holiday.name_en || '';
    }
    return holiday.name_en || holiday.name_vi || '';
  }, [language]);

  const detailData = useMemo(() => ({
    selectedDate,
    holidaysForSelectedDate,
    isSelectedFavorite,
    zodiacDisplay,
    selectedZodiacAnimal,
    zodiacEmoji,
    selectedZodiacInfo,
    selectedZodiacSign,
    westernZodiacSymbol,
    dayCanChi,
    auspiciousHours,
    selectedNotes
  }), [
    auspiciousHours,
    dayCanChi,
    holidaysForSelectedDate,
    isSelectedFavorite,
    selectedDate,
    selectedNotes,
    selectedZodiacAnimal,
    selectedZodiacInfo,
    selectedZodiacSign,
    westernZodiacSymbol,
    zodiacDisplay,
    zodiacEmoji
  ]);

  const detailHandlers = useMemo(() => ({
    getHolidayName,
    onToggleFavorite: handleToggleFavorite,
    formatHourLabel
  }), [formatHourLabel, getHolidayName, handleToggleFavorite]);

  return (
    <div className="app">
      <header className="header">
        <div className="container header-content">
          <h1 className="header-title">{t.title}</h1>
          <div className="action-row">
            <button
              onClick={() => setLanguage(language === 'vi' ? 'en' : 'vi')}
              className="language-button"
            >
              <Globe size={18} />
              {t.languageToggle}
            </button>
          </div>
        </div>
      </header>

      <main className="container main-content">
        <div className="home-tabs">
          {[{ id: 'calendar', label: t.homeTabs.calendar }, { id: 'bazi', label: t.homeTabs.bazi }].map((tab) => (
            <button
              key={tab.id}
              className={`home-tab ${mainTab === tab.id ? 'active' : ''}`}
              onClick={() => setMainTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {mainTab === 'calendar' ? (
          <CalendarTab
            t={t}
            language={language}
            currentDate={currentDate}
            monthData={monthData}
            selectedDate={selectedDate}
            selectedLunar={selectedLunar}
            localizedDayName={localizedDayName}
            localizedCanChiYear={localizedCanChiYear}
            lunarStemBranchLines={lunarStemBranchLines}
            weekdayLabel={weekdayLabel}
            favoriteSet={favoriteSet}
            monthlyHolidays={monthlyHolidays}
            holidaysLoading={holidaysLoading}
            holidaysError={holidaysError}
            detailData={detailData}
            detailHandlers={detailHandlers}
            onSelectDate={setSelectedDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onToday={handleToday}
            getHolidayName={getHolidayName}
          />
        ) : (
          <ErrorBoundary>
            <BaziAnalyzer language={language} />
          </ErrorBoundary>
        )}
      </main>
      <Footer texts={t} />
    </div>
  );
}
